import React, { useEffect, useRef, useState } from "react";
import { Row, Form, Col, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../../redux";
import { LoadingTrackedButton } from "../../../../Docuclipper/TrackedButton";
import DateFormatOptions from "./DateFormatOptions";
import { downloadSpreadsheetHelper } from "./utils";
import CsvColumnNames from "./CsvColumnNames";
import { toggleExportPreview } from "src/redux/reducers/DownloadInvoiceOptions";
import { setDownloadInvoiceModalOpen } from "src/redux/reducers/JobExport";
import { ExportType } from "src/docuclipper/DocuclipperTypes";
import { InvoiceFieldsSelector } from "./InvoiceFieldsSelector";
import {
  trackInvoiceExportAttempted,
  trackInvoiceExportSucceeded,
  trackInvoiceExportFailed,
  InvoiceExportTarget,
} from "src/utils/invoiceExportAnalytics";

type Props = {
  cta?: string;
  onExportSucceeded?: () => void;
};
interface PreviewTable {
  columns: string[];
  rows: (string | number)[][]; // Array of arrays of data that can be strings or numbers
}

export const CsvInvoiceOptions: React.FC<Props> = ({ cta = "Download", onExportSucceeded }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fileType, setFileType] = useState("CSV");
  const [previewTable, setPreviewTable] = useState<PreviewTable>({
    columns: [],
    rows: [],
  });

  const {
    selectedExportType,
    selectedCustomFormatType,
    sortBy,
    sortDirection,
    reloadExportPreview,
    options,
  } = useSelector((state: ReduxState) => state.DownloadInvoiceOptions);

  const { job } = useSelector((state: ReduxState) => state.JobData);

  const { downloadInvoiceModalOpen } = useSelector(
    (state: ReduxState) => state.JobExport
  );
  const isColumnConfigModalOpen = useSelector(
    (state: ReduxState) => state.ColumnConfig.isModalOpen
  );
  const prevColumnConfigModalOpenRef = useRef(false);

  const {
    downloadOptions,
    transactionLines,
    invoiceFieldExtra,
    invoiceFieldStandard,
    lineItemFieldExtra,
    lineItemFieldStandard,
  } = useSelector((state: ReduxState) => state.InvoiceManager);
  const { T, T2, T3, T4 } = useSelector(
    (state: ReduxState) => state.GenericOptions
  );
  const tlIds = transactionLines.map((tl) => tl.id || "").filter((id) => id);

  const { selectedDocumentIds, selectedFieldNames } = downloadOptions;

  // Get available fields based on the current invoice data
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const onExport = ({ preview = false }) => {
    if (preview) {
      setPreviewLoading(true);
    } else {
      setLoading(true);
    }
    const exportTarget: InvoiceExportTarget =
      fileType.toUpperCase() === "CSV" ? "csv" : "excel";
    const startedAt = !preview ? Date.now() : 0;
    if (!preview) {
      trackInvoiceExportAttempted({
        target: exportTarget,
        invoiceCount: tlIds.length,
        fieldMapping: selectedCustomFormatType || undefined,
      });
    }
    return downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "Invoice",
      selectedCustomFormatType as ExportType,
      {
        T,
        T2,
        T3,
        T4,
        dateFormat: options[`docuclipper.prefs.dateFormat`] || "",
        fileType: fileType.toUpperCase(),
      },
      selectedDocumentIds,
      selectedFieldNames,
      tlIds,
      [
        {
          field: "fieldName",
          operator: "equals",
          value: "invoiceMode",
        },
      ],
      sortBy,
      sortDirection,
      fileType as "CSV" | "XLSX",
      preview,
      {
        invoiceFieldExtra,
        invoiceFieldStandard,
        lineItemFieldExtra,
        lineItemFieldStandard,
      }
    )
      .then((data) => {
        if (preview) {
          setPreviewTable({ columns: data.columns, rows: data.rows });
          setPreviewLoading(false);
        } else {
          trackInvoiceExportSucceeded({
            target: exportTarget,
            invoiceCount: tlIds.length,
            durationMs: Date.now() - startedAt,
          });
          if (onExportSucceeded) onExportSucceeded();
          dispatch(setDownloadInvoiceModalOpen(false));
          setLoading(false);
        }
      })
      .catch((err) => {
        if (preview) {
          setPreviewLoading(false);
        } else {
          trackInvoiceExportFailed({
            target: exportTarget,
            errorCode: err?.errorCode || err?.status,
            errorMessage: err?.message || String(err),
            durationMs: Date.now() - startedAt,
          });
          setLoading(false);
        }
      });
  };

  if (fileType === "XLSX") {
    cta = cta?.replace("CSV", "XLSX");
  }

  useEffect(() => {
    if (downloadInvoiceModalOpen) {
      onExport({
        preview: true,
      });
      // dispatch(setReloadExportPreview(false));
    }
  }, [downloadInvoiceModalOpen]);

  useEffect(() => {
    onExport({
      preview: true,
    });
  }, [reloadExportPreview]);

  useEffect(() => {
    const wasOpen = prevColumnConfigModalOpenRef.current;
    const isClosedNow = !isColumnConfigModalOpen;
    if (wasOpen && isClosedNow) {
      onExport({ preview: true });
    }
    prevColumnConfigModalOpenRef.current = isColumnConfigModalOpen;
  }, [isColumnConfigModalOpen]);

  useEffect(() => {
    dispatch(toggleExportPreview());
  }, [
    invoiceFieldExtra,
    invoiceFieldStandard,
    lineItemFieldExtra,
    lineItemFieldStandard,
  ]);
  return (
    <div>
      <Row>
        <Col className="my-2">
          <strong>File Type</strong>
          <div className="d-flex">
            <Form.Check
              value="CSV"
              checked={fileType === "CSV"}
              onChange={(e) => setFileType(e.currentTarget.value)}
              type="radio"
              label="CSV"
            />
            <Form.Check
              className="ml-2"
              value="XLSX"
              checked={fileType === "XLSX"}
              onChange={(e) => setFileType(e.currentTarget.value)}
              type="radio"
              label="XLSX"
            />
          </div>
        </Col>

        <Col className="my-2">
          <div>
            <div className="font-weight-bold">Date Format</div>
            <DateFormatOptions />
          </div>
        </Col>
      </Row>

      <CsvColumnNames selectedExportType={selectedExportType} />
      {previewTable.columns?.length > 0 && previewTable.rows?.length > 0 && (
        <>
          <strong>Preview</strong>
          {!previewLoading ? (
            <Row>
              <Col>
                <Table striped bordered hover className="m-0">
                  <thead>
                    <tr>
                      {previewTable.columns.map((column, idx) => (
                        <th key={idx}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewTable.rows.map((row, idx) => (
                      <tr key={idx}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          ) : (
            <div>Loading Preview...</div>
          )}
        </>
      )}

      <Row className="justify-content-end mx-1 mt-4">
        <LoadingTrackedButton
          isLoading={loading}
          className={`btn-save-${selectedExportType} m-2`}
          variant="primary"
          onClick={() => onExport({ preview: false })}
        >
          {`${cta}`}
        </LoadingTrackedButton>
      </Row>
    </div>
  );
};
export default CsvInvoiceOptions;
